import React, { useContext, useEffect, useState } from 'react';
import { bool, func, object } from 'prop-types';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { ToastContext } from './context/ToastContext';
import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';
import { LoadingContext } from './context/LoadingContext';
import { useCallback } from 'react';
import { Suspense } from 'react';
import LoadingFallback from './LoadingFallback';
import Breadcrumbs from './Breadcrumbs';
import { BreadcrumbContext } from './context/BreadcrumbContext';

const RECOVERY_EMAIL_VERIFIED_TEXT = 'Recovery email verified';
const RECOVERY_EMAIL_VERIFICATION_LINK_SENT_TEXT = 'Verification link has been sent to your recovery email. Please check your recovery email and follow the instructions.';

function ManageRecoveryEmail({ user, userFetched }) {
    const [recoveryEmailDetail, setRecoveryEmailDetail] = useState(null);
    const [recoveryEmail, setRecoveryEmail] = useState('');
    const [message, setMessage] = useState('');
    const { sendMessage } = useContext(ToastContext);
    const loadingContext = useContext(LoadingContext);
    const { startLoading, finishLoading } = loadingContext;
    const navigate = useNavigate();
    const {
        updateBreadcrumbs,
    } = useContext(BreadcrumbContext);
    const { appContext } = useContext(AppContext);
    const [loading, setLoading] = useState(false);

    const updateRecoveryEmail = useCallback(async (e) => {
        e.preventDefault();
        if (!recoveryEmail) {
            return;
        }
        try {
            setLoading(true);
            await startLoading();
            const url = new URL('/api/user/recoveryEmail', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                    recoveryEmail,
                }),
            });
            const { success, error } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }
            if (success) {
                sendMessage('Recovery email updated successfully', 'success');
                setMessage('');
                fetchRecoveryEmailDetail();
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
            await finishLoading();
        }
    }, [recoveryEmail, user, appContext.config.recaptchaSiteKey]);

    const sendRecoveryEmailVerificationLink = useCallback(async () => {
        if (loading) {
            return;
        }
        try {
            setLoading(true);
            await startLoading();
            const url = new URL('/api/user/recoveryEmail/verify', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                }),
            });
            const { success, error } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }
            if (success) {
                sendMessage('Verification link has been sent to your recovery email.', 'success');
                setMessage(RECOVERY_EMAIL_VERIFICATION_LINK_SENT_TEXT);
                fetchRecoveryEmailDetail();
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
            await finishLoading();
        }
    }, [user]);

    const fetchRecoveryEmailDetail = useCallback(async () => {
        try {
            startLoading();
            const response = await fetch(`${process.env.APP_URL}api/user/recoveryEmail`, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (data.recoveryEmail) {
                setRecoveryEmailDetail(data);
                setRecoveryEmail(data.recoveryEmail);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            finishLoading();
        }
    }, [user]);

    useEffect(() => {
        if (userFetched && !user) {
            navigate('/login.asp');
        }
        if (userFetched && user) {
            fetchRecoveryEmailDetail();
        }
    }, [userFetched, user]);
    useEffect(() => {
        updateBreadcrumbs([
            ['My account', '/myaccount.asp'],
            ['Recovery email', '/recovery-email'],
        ]);
    }, []);
    return (
        <Suspense
            fallback={<LoadingFallback />}>
            <div
                className={'section'}
            >
                {!userFetched ? <div>Loading...</div> : null}
                {userFetched && !user ? <div>Redirect to login...</div> : null}
                {userFetched && user ? (
                    <>
                        <h2
                            className={'hr-headline'}>
                            Recovery email
                        </h2>
                        <Breadcrumbs />
                        <Form
                            className={'mt-3'}
                            style={{ width: '50%' }}
                            onSubmit={updateRecoveryEmail}
                        >
                            <Form.Group
                                className={'mb-3'}
                                controlId={'updateRecoveryEmailForm.recoveryEmail'}
                            >
                                <Form.Label>Recovery Email</Form.Label>
                                <Form.Control
                                    type={'email'}
                                    placeholder={'Recovery email'}
                                    value={recoveryEmail}
                                    onChange={(e) => setRecoveryEmail(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            <Button
                                variant={'outline-primary'}
                                style={{ marginRight: '.5em' }}
                                onClick={() => navigate(-1)}
                                disabled={loading}
                            >
                                Back
                            </Button>
                            <Button
                                variant={'primary'}
                                type={'submit'}
                                disabled={loading}
                            >
                                {
                                    recoveryEmailDetail?.recoveryEmail ? 'Update' : 'Add'
                                }
                            </Button>
                        </Form>
                    </>
                ) : null}
                {message && <p
                    className={'my-3 text-success'}>{message}</p>}
                {recoveryEmailDetail?.recoveryEmailVerified && <p
                    className={'my-3 text-success'}>{RECOVERY_EMAIL_VERIFIED_TEXT}</p>}
                {!message && recoveryEmailDetail?.recoveryEmail && !recoveryEmailDetail?.recoveryEmailVerified &&
                    <div
                        className={'mt-5'}>
                        <p
                            className={'m-0 mb-2'}
                        >Your recovery email is not verified.</p>
                        <Button
                            variant={'primary'}
                            onClick={sendRecoveryEmailVerificationLink}
                            disabled={loading}
                        >
                            Verify now
                        </Button>
                    </div>
                }
            </div>
        </Suspense>
    );
}

ManageRecoveryEmail.propTypes = {
    user: object,
    userFetched: bool,
    reloadUserData: func
};

export default ManageRecoveryEmail;